import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Category, CategoryId, Expense, User } from "../api";
import {
  catMap,
  daysLeftInMonth,
  fmt,
  fmtFull,
  monthName,
} from "../constants";
import { intlLocale } from "../i18n";
import { Icon } from "../icons";

interface Props {
  user: User;
  expenses: Expense[];
  isDesktop: boolean;
  onOpenAdd: () => void;
  onOpenSettings: () => void;
  onDeleteExpense: (id: string) => void;
}

interface CatRow extends Category {
  total: number;
}

export function HomeScreen({
  user,
  expenses,
  isDesktop,
  onOpenAdd,
  onOpenSettings,
  onDeleteExpense,
}: Props) {
  const { t } = useTranslation();
  const now = new Date();
  const [filterCat, setFilterCat] = useState<CategoryId | null>(null);
  const totalSpent = expenses.reduce((s, e) => s + e.amount, 0);
  const remaining = user.budget - totalSpent;

  const categories = user.categories;
  const catLookup = useMemo(() => catMap(categories), [categories]);

  const byCat = useMemo(() => {
    const m: Record<string, number> = Object.fromEntries(
      categories.map((c) => [c.id, 0]),
    );
    expenses.forEach((e) => {
      m[e.cat] = (m[e.cat] ?? 0) + e.amount;
    });
    return m;
  }, [expenses, categories]);

  const sortedCats = useMemo<CatRow[]>(
    () =>
      categories
        .map((c) => ({ ...c, total: byCat[c.id] ?? 0 }))
        .sort((a, b) => b.total - a.total),
    [byCat, categories],
  );

  const grouped = useMemo(() => {
    const g: Record<string, Expense[]> = {};
    for (const e of expenses) {
      if (filterCat && e.cat !== filterCat) continue;
      const key = new Date(e.date).toDateString();
      (g[key] ||= []).push(e);
    }
    return Object.entries(g).sort(
      (a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime(),
    );
  }, [expenses, filterCat]);

  const toggleFilter = (id: CategoryId) =>
    setFilterCat((cur) => (cur === id ? null : id));

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Header
        user={user}
        now={now}
        isDesktop={isDesktop}
        onOpenAdd={onOpenAdd}
        onOpenSettings={onOpenSettings}
      />

      {isDesktop ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "420px 1fr",
            gap: 28,
            alignItems: "start",
          }}
        >
          <div
            style={{
              position: "sticky",
              top: 32,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <SummaryCard
              user={user}
              totalSpent={totalSpent}
              remaining={remaining}
              now={now}
            />
            <CategoryCard
              cats={sortedCats}
              totalSpent={totalSpent}
              selected={filterCat}
              onSelect={toggleFilter}
            />
          </div>
          <ExpensesPanel
            grouped={grouped}
            now={now}
            onDelete={onDeleteExpense}
            filterCat={filterCat}
            onClearFilter={() => setFilterCat(null)}
            catLookup={catLookup}
          />
        </div>
      ) : (
        <div
          style={{
            padding: "0 20px 100px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <SummaryCard
            user={user}
            totalSpent={totalSpent}
            remaining={remaining}
            now={now}
          />
          <CategoryCard
            cats={sortedCats}
            totalSpent={totalSpent}
            selected={filterCat}
            onSelect={toggleFilter}
          />
          <ExpensesPanel
            grouped={grouped}
            now={now}
            onDelete={onDeleteExpense}
            filterCat={filterCat}
            onClearFilter={() => setFilterCat(null)}
            catLookup={catLookup}
          />
        </div>
      )}

      {!isDesktop && (
        <button
          onClick={onOpenAdd}
          aria-label={t("home.addExpense")}
          style={{
            position: "fixed",
            bottom: 24,
            right: 20,
            width: 58,
            height: 58,
            borderRadius: 999,
            background: "var(--ink)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              "0 10px 28px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.1)",
            zIndex: 40,
          }}
        >
          {Icon.plus("#fff", 22)}
        </button>
      )}
    </div>
  );
}

function Header({
  user,
  now,
  isDesktop,
  onOpenAdd,
  onOpenSettings,
}: {
  user: User;
  now: Date;
  isDesktop: boolean;
  onOpenAdd: () => void;
  onOpenSettings: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: isDesktop ? "4px 0 28px" : "24px 20px 20px",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--muted)",
            letterSpacing: ".1em",
            textTransform: "uppercase",
          }}
        >
          @{user.username} · {t("home.personalBudget")}
        </div>
        <div
          style={{
            fontSize: isDesktop ? 28 : 22,
            fontWeight: 600,
            marginTop: 4,
            letterSpacing: "-.02em",
          }}
        >
          {monthName(now)}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {isDesktop && (
          <button
            onClick={onOpenAdd}
            style={{
              padding: "11px 18px",
              borderRadius: 999,
              background: "var(--ink)",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {Icon.plus("#fff", 16)} {t("home.addExpense")}
          </button>
        )}
        <button
          onClick={onOpenSettings}
          aria-label={t("home.openSettings")}
          style={{
            width: 40,
            height: 40,
            borderRadius: 999,
            background: "var(--surface)",
            border: "1px solid var(--line)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--ink)",
          }}
        >
          {Icon.gear()}
        </button>
      </div>
    </div>
  );
}

function SummaryCard({
  user,
  totalSpent,
  remaining,
  now,
}: {
  user: User;
  totalSpent: number;
  remaining: number;
  now: Date;
}) {
  const { t } = useTranslation();
  const showDaily = user.preferences.showDailyAllowance;
  const dailyLeft = Math.max(0, remaining / Math.max(1, daysLeftInMonth(now)));
  return (
    <div
      style={{
        background: "var(--surface)",
        borderRadius: 16,
        border: "1px solid var(--line)",
        padding: "26px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <BudgetRing budget={user.budget} spent={totalSpent} />
      <div
        style={{
          marginTop: 22,
          width: "100%",
          display: "grid",
          gridTemplateColumns: showDaily ? "1fr 1fr 1fr" : "1fr 1fr",
          borderTop: "1px solid var(--line-2)",
          paddingTop: 18,
        }}
      >
        <Stat label={t("home.spent")} value={fmtFull(totalSpent)} />
        <Stat label={t("home.budget")} value={fmtFull(user.budget)} divider />
        {showDaily && (
          <Stat label={t("home.dailyLeft")} value={fmt(dailyLeft)} divider />
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  divider,
}: {
  label: string;
  value: string;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        textAlign: "center",
        borderLeft: divider ? "1px solid var(--line-2)" : "none",
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          color: "var(--muted)",
          textTransform: "uppercase",
          letterSpacing: ".1em",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        className="num"
        style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}
      >
        {value}
      </div>
    </div>
  );
}

function CategoryCard({
  cats,
  totalSpent,
  selected,
  onSelect,
}: {
  cats: CatRow[];
  totalSpent: number;
  selected: CategoryId | null;
  onSelect: (id: CategoryId) => void;
}) {
  const { t } = useTranslation();
  return (
    <Panel
      title={t("home.categories")}
      subtitle={selected ? t("home.tapToClear") : t("home.tapToFilter")}
    >
      {cats.map((c, i) => {
        const catPct = totalSpent > 0 ? c.total / totalSpent : 0;
        const active = c.total > 0;
        const isSelected = selected === c.id;
        return (
          <div
            key={c.id}
            onClick={() => onSelect(c.id)}
            role="button"
            aria-pressed={isSelected}
            style={{
              padding: "13px 18px",
              borderTop: i === 0 ? "none" : "1px solid var(--line-2)",
              display: "flex",
              alignItems: "center",
              gap: 14,
              cursor: "pointer",
              background: isSelected ? "var(--surface-2)" : "transparent",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: active ? "var(--ink)" : "var(--surface-2)",
                color: active ? "#fff" : "var(--muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 16,
                lineHeight: 1,
              }}
            >
              <span aria-hidden="true">{c.emoji}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 500 }}>{c.label}</span>
                <span
                  className="num"
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: active ? "var(--ink)" : "var(--muted)",
                  }}
                >
                  {active ? fmtFull(c.total) : "—"}
                </span>
              </div>
              <div
                style={{
                  marginTop: 6,
                  height: 2,
                  background: "var(--line-2)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${catPct * 100}%`,
                    background: "var(--ink)",
                    borderRadius: 999,
                    transition: "width .3s",
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </Panel>
  );
}

function ExpensesPanel({
  grouped,
  now,
  onDelete,
  filterCat,
  onClearFilter,
  catLookup,
}: {
  grouped: [string, Expense[]][];
  now: Date;
  onDelete: (id: string) => void;
  filterCat: CategoryId | null;
  onClearFilter: () => void;
  catLookup: Record<string, Category>;
}) {
  const { t } = useTranslation();
  const today = now.toDateString();
  const yesterday = new Date(now.getTime() - 86400000).toDateString();
  const total = grouped.reduce((s, [, items]) => s + items.length, 0);
  const filterLabel = filterCat ? catLookup[filterCat]?.label ?? null : null;

  return (
    <Panel
      title={
        filterLabel
          ? t("home.recentWith", { label: filterLabel })
          : t("home.recentExpenses")
      }
      subtitle={t("home.totalCount", { count: total })}
    >
      {filterCat && (
        <div
          style={{
            padding: "10px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--surface-2)",
            fontSize: 12.5,
          }}
        >
          <span style={{ color: "var(--muted)" }}>
            {t("home.filteredByPrefix")}{" "}
            <strong style={{ color: "var(--ink)" }}>{filterLabel}</strong>
          </span>
          <button
            onClick={onClearFilter}
            style={{
              padding: "5px 10px",
              borderRadius: 999,
              background: "var(--ink)",
              color: "#fff",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {t("common.clear")}
          </button>
        </div>
      )}
      {grouped.length === 0 && (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            color: "var(--muted)",
            fontSize: 14,
          }}
        >
          {filterCat
            ? t("home.noExpensesCategory")
            : t("home.noExpensesMonth")}
        </div>
      )}
      {grouped.map(([day, items], gi) => {
        const d = new Date(day);
        const label =
          day === today
            ? t("home.today")
            : day === yesterday
              ? t("home.yesterday")
              : d.toLocaleString(intlLocale(), {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                });
        const dayTotal = items.reduce((s, e) => s + e.amount, 0);
        return (
          <div key={day}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "14px 18px 6px",
                fontSize: 11.5,
                color: "var(--muted)",
                fontWeight: 600,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                borderTop: gi === 0 ? "none" : "1px solid var(--line-2)",
                background: "var(--surface-2)",
              }}
            >
              <span>{label}</span>
              <span className="num">{fmtFull(dayTotal)}</span>
            </div>
            {items.map((e) => (
              <ExpenseRow
                key={e.id}
                exp={e}
                cat={catLookup[e.cat]}
                onDelete={() => onDelete(e.id)}
              />
            ))}
          </div>
        );
      })}
    </Panel>
  );
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: "var(--surface)",
        borderRadius: 16,
        border: "1px solid var(--line)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px 18px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <div
          style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: "-.005em" }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 500 }}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div style={{ borderTop: "1px solid var(--line-2)" }}>{children}</div>
    </div>
  );
}

function ExpenseRow({
  exp,
  cat,
  onDelete,
}: {
  exp: Expense;
  cat: Category | undefined;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = cat?.label ?? t("home.uncategorized");
  const emoji = cat?.emoji ?? "•";
  return (
    <div
      onClick={() => setOpen((o) => !o)}
      style={{
        padding: "12px 18px",
        borderTop: "1px solid var(--line-2)",
        display: "flex",
        alignItems: "center",
        gap: 14,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "var(--surface-2)",
          color: "var(--ink-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          border: "1px solid var(--line-2)",
          fontSize: 16,
          lineHeight: 1,
        }}
      >
        <span aria-hidden="true">{emoji}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {exp.note || label}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
          {label}
        </div>
      </div>
      <div className="num" style={{ fontSize: 14.5, fontWeight: 600 }}>
        −{fmtFull(exp.amount)}
      </div>
      {open && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{
            marginLeft: 4,
            padding: "6px 10px",
            borderRadius: 8,
            background: "var(--danger-soft)",
            color: "var(--danger)",
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {t("common.delete")}
        </button>
      )}
    </div>
  );
}

function BudgetRing({ budget, spent }: { budget: number; spent: number }) {
  const { t } = useTranslation();
  const size = 220;
  const stroke = 9;
  const r = size / 2 - stroke;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(1, spent / budget);
  const over = spent > budget;
  const remaining = budget - spent;
  const dash = circ * pct;

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg
        width={size}
        height={size}
        style={{ transform: "rotate(-90deg)" }}
        aria-hidden="true"
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--line)"
          strokeWidth={stroke}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={over ? "var(--danger)" : "var(--ink)"}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          style={{ transition: "stroke-dasharray .5s ease" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: "var(--muted)",
            letterSpacing: ".1em",
            textTransform: "uppercase",
          }}
        >
          {over ? t("home.overBy") : t("home.remaining")}
        </div>
        <div
          className="num"
          style={{
            fontSize: 36,
            fontWeight: 600,
            marginTop: 6,
            letterSpacing: "-.025em",
            color: over ? "var(--danger)" : "var(--ink)",
          }}
        >
          {fmtFull(Math.abs(remaining))}
        </div>
        <div
          className="num"
          style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}
        >
          {t("home.ofBudget", { amount: fmtFull(budget), pct: Math.round(pct * 100) })}
        </div>
      </div>
    </div>
  );
}
